import { FC, useEffect } from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  InputLabelProps as MuiInputLabelProps,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";
import { useIntl } from "react-intl";
import { Controller, get, useFormContext, useWatch } from "react-hook-form";

export type IDroplistInputProps = {
  name: string;
  options: Record<string, string>;
  disabledOptions?: Array<string>;
  InputLabelProps?: Partial<MuiInputLabelProps>;
  helperText?: string;
} & SelectProps;

export const DroplistInput: FC<IDroplistInputProps> = props => {
  const {
    options,
    label,
    name,
    multiple,
    variant = "outlined",
    disabledOptions = [],
    InputLabelProps = {},
    helperText,
    ...rest
  } = props;

  const suffix = name.split(".").pop() as string;

  const form = useFormContext<any>();
  const formValues = useWatch();

  const error = get(form.formState.errors, name);

  const { formatMessage } = useIntl();
  const localizedLabel = label === void 0 ? formatMessage({ id: `form.labels.${suffix}` }) : label;
  const localizedHelperText = error ? formatMessage({ id: error.message }, { label: localizedLabel }) : "";

  useEffect(() => {
    return () => {
      form.resetField(name);
    };
  }, []);

  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <FormControl fullWidth sx={{ my: 1 }}>
          <InputLabel id={`${name}-select-label`} variant={variant} {...InputLabelProps}>
            {localizedLabel}
          </InputLabel>
          <Select
            multiple={multiple}
            labelId={`${name}-select-label`}
            label={localizedLabel}
            id={`${name}-select`}
            variant={variant}
            error={!!error}
            renderValue={value =>
              Array.isArray(value) ? value.map(v => options[v]).join(", ") : options[value as string]
            }
            {...field}
            value={get(formValues, name) ?? (multiple ? [] : "")}
            onChange={(e: any) => {
              form.setValue(name, e.target.value, { shouldTouch: true, shouldDirty: true });
            }}
            {...rest}
          >
            {Object.entries(options).map(([key, option], i) => (
              <MenuItem value={key} key={i} disabled={disabledOptions.includes(option)}>
                {option}
              </MenuItem>
            ))}
          </Select>

          <FormHelperText
            id={`${name}-helper-text`}
            error={!!error || !!helperText}
            sx={{
              ml: 0,
              position: "absolute",
              bottom: "-20px",
              opacity: localizedHelperText || helperText ? 1 : 0,
              transition: "opacity 0.2s",
              "@media (max-width: 1560px)": {
                bottom: "-40px",
              },
              "@media (max-width: 1200px)": {
                bottom: "-60px",
              },
            }}
          >
            {localizedHelperText || helperText || "\u00A0"}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};
